import React, { useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Loader2, Lock, Loader } from "lucide-react";
import { toast } from "sonner";

function ForceChangePassword() {
  const { changePassword } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await changePassword(null, pw);
      toast.success("Password updated");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed to update password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="force-change-password">
      <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <div className="w-11 h-11 rounded-full bg-[#ab0000]/10 flex items-center justify-center mb-4"><Lock size={20} className="text-[#ab0000]" /></div>
        <h2 className="font-display text-xl font-bold text-slate-900">Set a new password</h2>
        <p className="text-slate-500 text-sm mt-1">For security, please change your temporary password before continuing.</p>
        <form onSubmit={submit} className="space-y-3 mt-6">
          <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" data-testid="new-password"
            className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#ab0000] outline-none text-sm" />
          <input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password" data-testid="confirm-password"
            className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#ab0000] outline-none text-sm" />
          <button type="submit" disabled={loading} data-testid="change-password-submit"
            className="w-full h-11 rounded-lg bg-[#ab0000] hover:bg-[#8a0000] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 size={16} className="animate-spin" />} Update password
          </button>
        </form>
      </div>
    </div>
  );
}

export default function ProtectedRoute() {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader className="animate-spin text-[#ab0000]" size={28} />
      </div>
    );
  }
  if (user === false) {
    // Preserve the requested deep-link (e.g. a scanned Die QR) so we can return after sign-in.
    const rt = location.pathname + location.search;
    if (rt && rt !== "/") sessionStorage.setItem("returnTo", rt);
    return <Navigate to="/login" replace />;
  }
  // Skip forced password change while impersonating (Super Admin must keep the Exit banner).
  if (user.must_change_password && !user.impersonating) return <ForceChangePassword />;
  return <Outlet />;
}
